import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Mail,
  Phone,
  MapPin,
  ArrowUp,
  Clock,
  Sparkles,
  X,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";
import wechat from "../Images/qrcode.png"; 
import whats from "../Images/wat.png"; 
import { FaWhatsapp, FaWeixin, FaEnvelope } from "react-icons/fa";

const Footer = () => {
  const [activeModal, setActiveModal] = useState(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const QRModal = ({ title, onClose }) => {
    const qrImage = title === "WhatsApp" ? whats : wechat;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="qr-modal-title"
      >
        <div
          className="bg-gradient-to-r from-sky-700 to-sky-900 p-6 sm:p-8 rounded-lg max-w-sm w-full relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-sky-200 hover:text-white transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <h2 
            id="qr-modal-title"
            className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-center text-sky-200"
          >
            Scan {title} QR Code
          </h2>
          <div className="flex justify-center">
            <img
              src={qrImage}
              alt={`${title} QR Code for connecting with Gortherm Energy`}
              className="w-56 h-56 sm:w-72 sm:h-72 object-cover rounded-lg shadow-lg"
              loading="lazy"
            />
          </div>
          <p className="text-center mt-4 sm:mt-6 text-sky-200 text-base sm:text-lg">
            Scan to connect with us on {title}
          </p>
        </div>
      </div>
    );
  };

  // Footer Schema for SEO
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Gortherm Energy Pvt Ltd",
    "url": "https://www.gorthermenergy.com",
    "logo": "https://www.gorthermenergy.com/logo.png",
    "description": "Leading provider of industrial heating solutions and energy management systems across Nepal",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Yashoda Bhawan, H08",
      "addressLocality": "Biratnagar",
      "postalCode": "56613",
      "addressCountry": "NP"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+977-9802781400",
      "email": "info@gorthermenergy.com",
      "contactType": "Customer Service",
      "areaServed": "NP",
      "availableLanguage": ["English", "Nepali"]
    },
    "sameAs": [
      "https://www.facebook.com/gorthermenergy/",
      "https://www.instagram.com/gorthermenergypvtltd/",
      "https://www.linkedin.com/company/gortherm-energy"
    ]
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema, null, 2)}
        </script>
      </Helmet>

      <footer className="relative bg-gradient-to-r from-sky-600 to-sky-800" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-8 sm:pb-12">
          
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 lg:gap-16 mb-12 sm:mb-16">
            
            {/* Company Info */}
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center space-x-3 group">
                <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 text-red-500 transform group-hover:rotate-12 transition-transform duration-300" aria-hidden="true" />
                <span className="text-2xl sm:text-3xl font-bold text-white relative">
                  Gortherm Energy
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-red-500 group-hover:w-full transition-all duration-500" aria-hidden="true"></span>
                </span>
              </div>
              <p className="text-sky-200 text-sm sm:text-base leading-relaxed">
                Leading provider of industrial boiler solutions, steam accessories, and energy management systems across Nepal since 2015.
              </p>
              <address className="space-y-3 sm:space-y-5 not-italic">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0 mt-1" aria-hidden="true" />
                  <span className="text-sky-200 text-sm sm:text-base">
                    Yashoda Bhawan, H08, Biratnagar 56613, Nepal
                  </span>
                </div>
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0" aria-hidden="true" />
                  <a href="tel:+9779802781400" className="text-sky-200 text-sm sm:text-base hover:text-white transition-colors">
                    +977-9802781400
                  </a>
                </div>
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <FaEnvelope className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0" aria-hidden="true" />
                  <a href="mailto:info@gorthermenergy.com" className="text-sky-200 text-sm sm:text-base hover:text-white transition-colors break-all">
                    info@gorthermenergy.com
                  </a>
                </div>
              </address>
            </div>

            {/* Quick Links */}
            <nav className="space-y-4 sm:space-y-6" aria-labelledby="quick-links-heading">
              <h3 id="quick-links-heading" className="text-xl sm:text-2xl font-semibold text-white">
                Quick Links
              </h3>
              <ul className="space-y-3 sm:space-y-4">
                {[
                  { name: "Home", path: "/" },
                  { name: "About Us", path: "/about" },
                  { name: "Products", path: "/products" },
                  { name: "Services", path: "/services" },
                  { name: "Contact", path: "/contact" },
                 {name : "Catalouge", path :"https://gortherm-energy-catelouge.vercel.app/"}
                ].map(({ name, path }) => (
                  <li key={name} className="flex items-center space-x-2 sm:space-x-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" aria-hidden="true"></span>
<a
  href={path}
  target={path.startsWith("http") ? "_blank" : "_self"}
  rel={path.startsWith("http") ? "noopener noreferrer" : ""}
  className="text-sky-200 hover:text-red-400 transition-colors duration-300 text-sm sm:text-base"
>
  {name}
</a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Legal */}
            <nav className="space-y-4 sm:space-y-6" aria-labelledby="legal-heading">
              <h3 id="legal-heading" className="text-xl sm:text-2xl font-semibold text-white">
                Legal
              </h3>
              <ul className="space-y-3 sm:space-y-4">
                {[
                  { name: "Privacy Policy", path: "/Legal/privacy" },
                  { name: "Terms of Service", path: "/Legal/termservice" },
                  { name: "Disclaimer", path: "/Legal/disclaimer" },
                ].map(({ name, path }) => (
                  <li key={name} className="flex items-center space-x-2 sm:space-x-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" aria-hidden="true"></span>
                    <a
                      href={path}
                      className="text-sky-200 hover:text-red-400 transition-colors duration-300 text-sm sm:text-base"
                    >
                      {name}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Social Media */}
            <div className="space-y-4 sm:space-y-6">
              <h3 className="text-xl sm:text-2xl font-semibold text-white">
                Stay Connected
              </h3>
              <p className="text-sky-200 text-sm sm:text-base">
                Follow us on social media for updates and industry insights
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                {[
                  {
                    Icon: Instagram,
                    color: "bg-gradient-to-br from-pink-500 to-red-500",
                    link: "https://www.instagram.com/gorthermenergypvtltd/",
                    label: "Instagram"
                  },
                  {
                    Icon: Facebook,
                    color: "bg-blue-600",
                    link: "https://www.facebook.com/gorthermenergy/",
                    label: "Facebook"
                  },
                  {
                    Icon: Linkedin,
                    color: "bg-blue-700",
                    link: "https://www.linkedin.com/company/gortherm-energy",
                    label: "LinkedIn"
                  },
                  {
                    Icon: FaWhatsapp,
                    color: "bg-green-500",
                    modal: "WhatsApp",
                    label: "WhatsApp"
                  },
                  {
                    Icon: FaWeixin,
                    color: "bg-green-600",
                    modal: "WeChat",
                    label: "WeChat"
                  },
                ].map(({ Icon, color, link, modal, label }) => (
                  <div key={label}>
                    {modal ? (
                      <button
                        onClick={() => setActiveModal(modal)}
                        className={`${color} p-3 sm:p-4 rounded-full text-white shadow-lg hover:scale-110 transform transition-all duration-300 hover:rotate-6`}
                        aria-label={`Open ${label} QR code`}
                      >
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                      </button>
                    ) : (
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${color} p-3 sm:p-4 rounded-full text-white shadow-lg hover:scale-110 transform transition-all duration-300 hover:rotate-6 inline-flex`}
                        aria-label={`Visit our ${label} page`}
                      >
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>


          {/* Copyright */}
          <div className="pt-6 sm:pt-8 border-t border-sky-700/50">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              <p className="text-sky-200 text-sm sm:text-base text-center sm:text-left">
                © 2024 Gortherm Energy Pvt Ltd. All rights reserved.
              </p>
              <p className="text-sky-200 text-xs sm:text-sm text-center sm:text-right">
                Industrial Boiler Solutions | Steam Accessories | Energy Management
              </p>
            </div>
          </div>
        </div>

        {/* Scroll to Top Button */}
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 p-3 sm:p-4 bg-gradient-to-r from-red-600 to-red-600 
            rounded-full shadow-lg hover:from-sky-700 hover:to-sky-800 
            transition-all duration-300 transform hover:scale-110 z-40"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </button>
      </footer>

      {/* QR Modal */}
      {activeModal && (
        <QRModal title={activeModal} onClose={() => setActiveModal(null)} />
      )}
    </>
  );
};

export default Footer;